/**
 * Featured Nutrients — 16-cell grid of small RDA progress donuts.
 * Shared between Diet Recall and Meal Plan.
 */
import * as React from "react";
import { Pie, PieChart } from "recharts";

import {
  Widget,
  WidgetContent,
  WidgetHeader,
} from "@/components/consultation/shared/Widget";
import { ChartContainer, type ChartConfig } from "@/components/ui/chart";
import type { MealData } from "@/types/nutrition";

interface FeaturedNutrientsCardProps {
  totalNutrients: MealData["total_nutrients"] | null;
}

// Featured nutrients matching Flutter's _extractFeaturedNutrients (16 nutrients)
//
// `key` indexes `total_nutrients` by literal nutrient NAME, so a backend merge of
// duplicate NutrientType rows can blank a tile with no error. Columbus guards
// these: CONSOLE_NUTRIENT_KEYS in
// nutrition/tests_merge_duplicate_nutrient_types.py mirrors every key below and
// fails CI if a merge retires one. Adding an entry or changing a key? Tell
// backend to add it there — a key absent from that list is unprotected.
const FEATURED_NUTRIENTS = [
  { name: "Calcium", category: "minerals", key: "Calcium" },
  { name: "Iron", category: "minerals", key: "Iron" },
  { name: "Magnesium", category: "minerals", key: "Magnesium" },
  { name: "Selenium", category: "minerals", key: "Selenium" },
  { name: "Vitamin C", category: "vitamins", key: "Ascorbic_acid_C" },
  { name: "Zinc", category: "minerals", key: "Zinc" },
  { name: "Cholesterol", category: "lipids", key: "Cholesterol" },
  { name: "Omega 3", category: "lipids", key: "Alpha Linolenic/Omega 3" },
  { name: "Omega 6", category: "lipids", key: "Linoleic/Omega 6" },
  { name: "Vitamin A", category: "vitamins", key: "Retinol_vit_A" },
  { name: "Vitamin B1", category: "vitamins", key: "Thiamine_B1" },
  { name: "Fiber", category: "carbohydrates", key: "Fiber" },
  { name: "Vitamin B6", category: "vitamins", key: "Pyridoxin_B6" },
  { name: "Vitamin B9", category: "vitamins", key: "Folates_B9" },
  { name: "Vitamin D", category: "vitamins", key: "Vitamin_D" },
  { name: "Added Sugar", category: "carbohydrates", key: "Free Sugar" },
];

const FEATURED_NUTRIENT_CHART_CONFIG = {
  value: { label: "RDA" },
  filled: { label: "Filled", color: "oklch(var(--primary))" },
  remaining: { label: "Remaining", color: "oklch(var(--muted))" },
} satisfies ChartConfig;

export const FeaturedNutrientsCard: React.FC<FeaturedNutrientsCardProps> = ({
  totalNutrients,
}) => {
  if (!totalNutrients) return null;

  const nutrients = totalNutrients as unknown as Record<
    string,
    Record<
      string,
      { amount: number; unit: string; rda_percentage: number | null }
    >
  >;

  return (
    <Widget>
      <WidgetHeader title="Featured Nutrients" />
      <WidgetContent>
        <div className="grid grid-cols-8 gap-4">
          {FEATURED_NUTRIENTS.map((nutrient) => {
            const data = nutrients?.[nutrient.category]?.[nutrient.key];
            const rawPercentage = data?.rda_percentage ?? 0;
            const hasValue = rawPercentage > 0;
            const filled = Math.min(100, rawPercentage);
            const remaining = Math.max(0, 100 - filled);
            const chartData = hasValue
              ? [
                  {
                    name: "filled",
                    value: filled,
                    fill: "var(--color-filled)",
                  },
                  {
                    name: "remaining",
                    value: remaining,
                    fill: "var(--color-remaining)",
                  },
                ]
              : [
                  {
                    name: "remaining",
                    value: 1,
                    fill: "var(--color-remaining)",
                  },
                ];

            return (
              <div
                key={nutrient.name}
                className="flex flex-col items-center gap-2"
              >
                <div className="relative w-16 h-16">
                  <ChartContainer
                    config={FEATURED_NUTRIENT_CHART_CONFIG}
                    className="aspect-square w-16 h-16"
                  >
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={22}
                        outerRadius={30}
                        startAngle={90}
                        endAngle={-270}
                        stroke="none"
                        isAnimationActive={false}
                      />
                    </PieChart>
                  </ChartContainer>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span
                      className={
                        hasValue
                          ? "z-caption text-foreground tabular-nums"
                          : "z-caption text-muted-foreground tabular-nums"
                      }
                    >
                      {hasValue ? `${rawPercentage.toFixed(0)}%` : "N/A"}
                    </span>
                  </div>
                </div>
                <span className="z-caption text-center text-muted-foreground">
                  {nutrient.name}
                </span>
              </div>
            );
          })}
        </div>
      </WidgetContent>
    </Widget>
  );
};
